import { ChangeDetectorRef, Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { IconComponent } from '../icon/icon.component';
import { AuthService } from '../../../auth/auth.service';
import { ProfileService, UserProfile } from '../../../profile/profile.service';
import { InstructorOrganizationService } from '../../../instructor/instructor-organization.service';
import { AdminOrganizationService } from '../../../admin/admin-organization.service';
import { PermissionService } from '../../services/permission.service';
import { HasPermissionDirective } from '../../directives/has-permission.directive';
import { OrganizationSwitcherComponent } from '../organization-switcher/organization-switcher.component';
import { DebugMenuComponent } from '../debug-menu/debug-menu.component';
import { NotificationStateService } from '../../services/notification-state.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, IconComponent, HasPermissionDirective, OrganizationSwitcherComponent, DebugMenuComponent],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  currentUser: UserProfile | null = null;
  selectedOrgName: string | null = null;
  selectedOrgId: string | null = null;
  isLoadingOrg: boolean = false;
  orgLoadError: string | null = null;
  notificationCount = 0;
  private organizationSubscription?: Subscription;
  private userOrganizationsSubscription?: Subscription;
  private notificationSubscription?: Subscription;
  private storageListener: ((e: StorageEvent) => void) | null = null;

  // Cache permission values to reduce repeated checks
  private cachedInstructorAccess: boolean | null = null;
  private cachedAdminAccess: boolean | null = null;
  
  constructor(
    private router: Router,
    private authService: AuthService,
    private profileService: ProfileService,
    private orgService: InstructorOrganizationService,
    private adminOrgService: AdminOrganizationService,
    private permissionService: PermissionService,
    private notificationStateService: NotificationStateService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadUserData();
    this.loadOrganizationData();

    // Subscribe to shared notification count (no polling per component)
    this.notificationSubscription = this.notificationStateService.notificationCount$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(count => {
      this.notificationCount = count;
      // Force change detection to update the badge immediately
      this.cdr.detectChanges();
    });

    // KAN-400: live-sync the rendered user identity (name + avatar initial) the
    // instant the persisted current-user changes — e.g. after a profile-name
    // save in this same tab (the window `storage` event never fires for the
    // writing tab). currentUser$ is fed by the canonical setCurrentUser
    // write-path. Zoneless: markForCheck alone won't tick, so detectChanges().
    this.profileService.currentUser$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.currentUser = user;
      // Identity/role may have changed — drop cached permission answers.
      this.cachedInstructorAccess = null;
      this.cachedAdminAccess = null;
      this.cdr.detectChanges();
    });

    // Subscribe to organization changes to update sidebar when user switches organizations
    this.userOrganizationsSubscription = this.authService.userOrganizations$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      // Reload user data which invalidates permission cache and updates currentUser
      this.loadUserData();
      this.loadOrganizationData();
      this.cdr.markForCheck();
    });

    // KAN-670: the sidebar stays mounted across page changes, so it can no longer
    // count on being rebuilt after an org switch. The switcher updates the user
    // (firing userOrganizations$ above) BEFORE it sets the selected org id, so the
    // id read there is the org being left; follow the id stream itself.
    this.orgService.selectedOrgId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(orgId => {
      this.selectedOrgId = orgId;
      this.cdr.markForCheck();
    });

    // Listen for storage changes to keep user data in sync across tabs
    this.storageListener = (e: StorageEvent) => {
      if (e.key === 'userData' || e.key === 'selectedOrgId') {
        this.loadUserData();
        this.loadOrganizationData();
        this.cdr.markForCheck();
      }
    };
    window.addEventListener('storage', this.storageListener);
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    if (this.organizationSubscription) {
      this.organizationSubscription.unsubscribe();
    }
    if (this.userOrganizationsSubscription) {
      this.userOrganizationsSubscription.unsubscribe();
    }
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }

    // Clean up event listener
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
    }
  }

  private loadUserData(): void {
    this.currentUser = this.authService.getCurrentUser();
    // Invalidate permission cache when user data changes
    this.cachedInstructorAccess = null;
    this.cachedAdminAccess = null;
    if (!this.currentUser) {
      // If no user data, redirect to login
      this.router.navigate(['/auth/login']);
    }
  }

  private loadOrganizationData(): void {
    // Check for instructor role in current organization context
    const hasInstructorRole = this.hasInstructorRole(this.currentUser);
    
    if (hasInstructorRole) {
      this.selectedOrgId = this.orgService.getSelectedOrganizationId();
      if (this.selectedOrgId) {
        this.isLoadingOrg = true;
        this.orgLoadError = null;
        
        // Clean up existing subscription
        if (this.organizationSubscription) {
          this.organizationSubscription.unsubscribe();
        }
        
        // Subscribe to the reactive organization observable
        this.organizationSubscription = this.orgService.currentOrganization$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (org) => {
            if (org) {
              this.selectedOrgName = org.name;
              this.isLoadingOrg = false;
              this.cdr.markForCheck();
            } else {
              // If no current organization, try to load it
              this.orgService.getInstructorOrganization().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                next: (loadedOrg) => {
                  this.selectedOrgName = loadedOrg.name;
                  this.isLoadingOrg = false;
                  this.cdr.markForCheck();
                },
                error: (err) => {
                  console.error('Error loading organization:', err);
                  this.selectedOrgName = null;
                  this.orgLoadError = 'Failed to load organization';
                  this.isLoadingOrg = false;
                  this.cdr.markForCheck();
                }
              });
            }
          },
          error: (err) => {
            console.error('Error with organization observable:', err);
            this.selectedOrgName = null;
            this.orgLoadError = 'Failed to load organization';
            this.isLoadingOrg = false;
            this.cdr.markForCheck();
          }
        });
      }
    }
  }

  // Pure function to determine instructor navigation path
  private getInstructorNavigationPath(user: UserProfile | null, selectedOrgId: string | null): string {
    if (!user || !this.hasInstructorRole(user)) {
      return '/dashboard'; // Default fallback
    }
    
    // Always go to instructor dashboard - the dashboard will handle redirects if needed
    return '/instructor/dashboard';
  }


  // Pure function to determine organization change navigation path
  private getOrganizationChangeNavigationPath(user: UserProfile | null): string {
    if (!user) {
      return '/dashboard';
    }
    
    // Check if user is system admin
    if (user.isSystemAdmin || (user as any).is_system_admin) {
      return '/admin/organizations';
    }
    
    // Check if user has instructor role in current org
    if (this.hasInstructorRole(user)) {
      return '/instructor/organization';
    }
    
    return '/dashboard';
  }

  changeOrganization(): void {
    const path = this.getOrganizationChangeNavigationPath(this.currentUser);
    this.router.navigate([path]);
  }

  refreshUserData(): void {
    this.loadUserData();
    this.loadOrganizationData();
  }

  // Pure function to check if user has instructor role in current organization
  private hasInstructorRole(user: UserProfile | null): boolean {
    if (!user) return false;
    
    // Check system admin first
    if ((user as any).isSystemAdmin || (user as any).is_system_admin) {
      return true;
    }
    
    // Check current organization role for multi-org support
    const currentOrgRole = (user as any).currentOrgRole;
    if (currentOrgRole) {
      if (['admin', 'instructor'].includes(currentOrgRole)) {
        return true;
      }
    }

    // Fallback to legacy role field
    if (user.role) {
      if (['admin', 'instructor'].includes(user.role)) {
        return true;
      }
    }

    return false;
  }

  hasRole(role: string): boolean {
    if (!this.currentUser) return false;
    
    // For multi-org support, check currentOrgRole first
    const currentOrgRole = (this.currentUser as any).currentOrgRole;
    if (currentOrgRole === role) {
      return true;
    }
    
    // Fallback to legacy role field
    return this.currentUser.role === role;
  }

  hasPermission(permission: string): boolean {
    return this.permissionService.hasPermission(permission);
  }

  canAccessAdmin(): boolean {
    // Use cached value if available
    if (this.cachedAdminAccess !== null) {
      return this.cachedAdminAccess;
    }
    // Cache and return the result
    this.cachedAdminAccess = this.permissionService.canAccessAdmin();
    return this.cachedAdminAccess;
  }

  canAccessInstructor(): boolean {
    // Use cached value if available
    if (this.cachedInstructorAccess !== null) {
      return this.cachedInstructorAccess;
    }
    // Cache and return the result
    this.cachedInstructorAccess = this.permissionService.canAccessInstructor();
    return this.cachedInstructorAccess;
  }

  isAuthenticated(): boolean {
    return this.authService.isAuthenticated() && this.currentUser !== null;
  }
  

  logout(): void {
    // Clear organization state first to trigger cleanup in subscribed components
    this.orgService.clearSelectedOrganization();
    this.adminOrgService.clearSelectedOrganization();
    // Then clear auth data
    this.authService.logout();
    // Navigate to login
    this.router.navigate(['/auth/login']);
  }
}